import { of, concat, from } from 'rxjs';
import { mergeMap, map, catchError, filter, switchMap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import {
  ballSet,
  ballError,
  ballHandleForm,
  ballsListLoad,
  ballsListLoaded,
  ballSavePhoto,
  ballPhotoSaved,
  types,
  pages,
  ballSelect,
} from './arsenal.redux';
import { sidebarMenuItemSelected } from 'components/sideBar/sideBar.redux';
import { types as spectreAppTypes } from 'components/spectreApp/spectreApp.redux';
import { types as bowlersTypes } from 'components/bowlers/bowlers.redux';
import { datasources } from '../db/datasources';
import { takePhoto } from 'components/fileSystem/takePhoto';
import { galleryPhoto } from 'components/fileSystem/galleryPhoto';
import { fileSystem, isDesktop } from 'components/fileSystem';
import uuid from 'uuid/v1';
import * as R from 'ramda';
import * as Promise from 'bluebird';

const getLayout = currentLayout => ball => {
  switch (currentLayout) {
    case 'dual_angle':
      return [ball.drilling_angle || '', ball.pin_to_pap || '', ball.val_angle || ''].join(' X ');
    case 'vls':
      return [ball.pin_to_pap || '', ball.psa_to_pap || '', ball.pin_buffer || ''].join(' X ');
    case 'none':
    default:
      return ball.layout;
  }
};

const ballsHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballHandleForm),
    debounceTime(500),
    mergeMap(({ payload: { ball } }) => {
      ball.layout = getLayout(state$.value.settings.appSettings.arsenal_layout_style)(ball);
      const { ballDrilledDS } = datasources;
      if (ball.id) {
        return ballDrilledDS.update({ ...ball, localId: ball.id }).then(() => ballSet({ ball }));
      } else {
        const id = uuid();
        return ballDrilledDS.create({ ...ball, id }).then(() => {
          return ballSet({ ball: { ...ball, id } });
        });
      }
    })
  );

const ballsStateChangeEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballChangeState),
    mergeMap(({ payload: { ball, state } }) => {
      const { ballDrilledDS } = datasources;
      return ballDrilledDS.update({ ...ball, localId: ball.id, enable: state }).then(() => ballsListLoad());
    })
  );

const ballsRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballRemove),
    mergeMap(({ payload: { ball } }) => {
      const { ballDrilledDS } = datasources;
      return ballDrilledDS.delete(ball.id).then(() => {
        return ballsListLoad();
      });
    })
  );

const ballsPhotoSavedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballPhotoSaved),
    mergeMap(() => {
      return of(ballHandleForm({ ball: state$.value.balls.ball }));
    })
  );

const comparatorCaseInsensitive = property => R.compose(R.prop(property));

const sortByOrderCaseInsensitive = R.sortWith([R.ascend(comparatorCaseInsensitive('position'))]);

const ballsListLoadedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballsListLoad),
    mergeMap(({ payload: { selectFirst } = { selectFirst: false } }) => {
      const { ballDrilledDS } = datasources;
      return from(
        ballDrilledDS.read({
          customerId: state$.value.bowlers.bowler.id,
        })
      ).pipe(
        switchMap(data => {
          if (!state$.value.balls.ball.id) {
            return concat(
              of(
                ballSelect({
                  ball: R.defaultTo({})(
                    data.find(
                      ball =>
                        ball.id ===
                        state$.value.spectreApp.lastCustomerInfo[state$.value.spectreApp.lastCustomerInfo.lastCustomer]
                          .lastArsenal
                    )
                  ),
                  noChangePage: true,
                })
              ),
              of(
                ballsListLoaded({
                  balls: sortByOrderCaseInsensitive(data),
                  selectFirst,
                })
              )
            );
          } else {
            return of(
              ballsListLoaded({
                balls: sortByOrderCaseInsensitive(data),
                selectFirst,
              })
            );
          }
        })
      );
    })
  );

const ballsEpic = action$ =>
  action$.pipe(
    ofType(types.ballsChangePage),
    filter(action => action.payload.page === pages.list),
    switchMap(() => concat(of(ballsListLoad()), of(sidebarMenuItemSelected({ menuItemSelected: '3' }))))
  );

const ballsStartApplicationEpic = (action$, state$) =>
  action$.pipe(
    ofType(spectreAppTypes.spectreappCurrentPage),
    filter(action => action.payload.currentPage === 'arsenal' && state$.value.balls.currentPage === pages.list),
    mergeMap(() => of(ballsListLoad()))
  );

const ballsBowlerSelectEpic = (action$, state$) =>
  action$.pipe(
    ofType(bowlersTypes.bowlerSelect),
    mergeMap(() => of(ballsListLoad({ selectFirst: true })))
  );

const ballsAddNewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballAddNew),
    withLatestFrom(state$),
    mergeMap(() =>
      from(
        new Promise((success, reject) => {
          const { ballDrilledDS } = datasources;

          const id = uuid();

          ballDrilledDS
            .create({ localId: id, customer_id: state$.value.bowlers.bowler.id })
            .then(ball => {
              return success(ball);
            })
            .catch(error => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(ball => {
          return ballSelect({ ball: ball });
        }),
        catchError(error => {
          console.warn(error);
          return ballError({ error: {} });
        })
      )
    )
  );

const ballsTakePhotoEpic = action$ =>
  action$.pipe(
    ofType(types.ballTakePhoto),
    mergeMap(() =>
      from(takePhoto()).pipe(
        map(photoData => {
          return ballSavePhoto({ photoData });
        })
      )
    )
  );

const ballsGalleryPhotoEpic = action$ =>
  action$.pipe(
    ofType(types.ballGalleryPhoto),
    mergeMap(() =>
      from(galleryPhoto()).pipe(
        map(photoData => {
          return ballSavePhoto({ photoData });
        })
      )
    )
  );

const ballsRemovePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballRemovePhoto),
    mergeMap(() => of(ballHandleForm({ ball: state$.value.balls.ball })))
  );

const ballsSavePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballSavePhoto),
    withLatestFrom(state$),
    switchMap(([{ payload: { photoData: { realData, dataType } } }, state]) =>
      from(
        new Promise(success => {
          const { ball } = state.balls;
          const { savebase64AsImageFile, getImageUrl } = fileSystem();

          let fileName = 'arsenal_' + ball.id + '_' + uuid() + '.png';

          savebase64AsImageFile(fileName, realData, dataType).then(file => {
            if (isDesktop()) {
              file.url = getImageUrl(file.url).replace('cdvfile:', 'electronfile:');
            }
            success(file);
          });
        })
      ).pipe(
        map(photo => {
          console.log('Entro aqui epic');
          return ballPhotoSaved({ photo: photo.url });
        })
      )
    )
  );

const ballsChangeOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballsChangeOrder),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const { ballDrilledDS } = datasources;
      const pUpdate = state.balls.balls.map((ball, index) =>
        ballDrilledDS.update({ ...ball, localId: ball.id, position: index })
      );
      return Promise.all(pUpdate).then(() => ballsListLoad());
    })
  );

export default combineEpics(
  ballsEpic,
  ballsStartApplicationEpic,
  ballsListLoadedEpic,
  ballsHandleFormEpic,
  ballsAddNewEpic,
  ballsTakePhotoEpic,
  ballsGalleryPhotoEpic,
  ballsSavePhotoEpic,
  ballsPhotoSavedEpic,
  ballsRemovePhotoEpic,
  ballsRemoveEpic,
  ballsStateChangeEpic,
  ballsChangeOrderEpic,
  ballsBowlerSelectEpic
);
